/**
 * 저장 거절 안내 popover — 저장 버튼 아래에 붙는다.
 *
 * 검증은 어느 셀이 문제인지 이미 알고 있다. 그 정보를 토스트 한 줄로 요약해
 * 버리는 대신 여기서 건수·위치·사유를 보여주고, 해당 셀로 바로 이동시킨다.
 *
 * 닫히는 조건은 두 가지뿐이다: 문제 셀을 모두 고쳤을 때(= 목록이 빈다, 화면이
 * 판단) 또는 사용자가 X를 눌렀을 때. 다른 곳을 클릭해도 닫지 않는다 — 셀을
 * 고치려면 바깥을 클릭해야 하므로 outside-click으로 닫으면 쓸 수 없다.
 */

import { ChevronLeft, ChevronRight, TriangleAlert, X } from "lucide-react";

import { IconButton } from "@/toggle-ui-kit/icon-button";

export interface SaveRejectionPopoverProps {
  /** 아직 고치지 않은 거절 건수 */
  count: number;
  /** 현재 보고 있는 항목 순번 (1-based) */
  position: number;
  /** 표·항목 경로 */
  where: string;
  /** 저장이 막힌 입력값 */
  value: string;
  /** 거절 사유 */
  reason: string;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}

export const SaveRejectionPopover = ({
  count,
  position,
  where,
  value,
  reason,
  onPrev,
  onNext,
  onClose,
}: SaveRejectionPopoverProps) => (
  <div
    role="status"
    className="absolute right-0 top-[calc(100%+8px)] z-50 w-[320px] rounded-[10px] border border-line-normal bg-background-base p-4 shadow-2xl"
  >
    <div className="flex items-start gap-2">
      <TriangleAlert className="mt-0.5 size-4 shrink-0 text-status-caution" />
      <span className="label-1-normal-semibold flex-1 text-label-normal">
        저장되지 않은 값이 {count}건 있습니다
      </span>
      <IconButton
        icon={<X />}
        onClick={onClose}
        aria-label="저장 안내 닫기"
        className="-mr-1 -mt-1"
      />
    </div>

    <div className="mt-3 flex items-center justify-end gap-2">
      <span className="caption-1-regular text-label-alternative">
        {position}/{count}
      </span>
      <IconButton
        variant="outlined"
        icon={<ChevronLeft />}
        onClick={onPrev}
        disabled={position <= 1}
        aria-label="이전 셀로 이동"
      />
      <IconButton
        variant="outlined"
        icon={<ChevronRight />}
        onClick={onNext}
        disabled={position >= count}
        aria-label="다음 셀로 이동"
      />
    </div>

    <div className="mt-3 flex flex-col gap-1 border-t border-line-normal pt-3">
      <span className="caption-1-medium text-label-alternative">{where}</span>
      <span className="body-2-normal-regular text-label-normal">
        입력값 &quot;{value}&quot;
      </span>
      <span className="body-2-normal-regular text-status-negative">
        {reason}
      </span>
    </div>
  </div>
);
